// Copyright (c) 2026 Chain Capital Technologies Ltd. All rights reserved.
// See LICENSE file in the project root for full license information.

import { useEffect, useRef, useState } from 'preact/hooks';
import { api } from '../api.js';
import { useSession } from '../session-context.js';
import {
  isEmbedConfirmedFor,
  isEmbedErrorMessage,
  isEmbedReadyMessage,
  isEmbedRequestSignature,
  isEmbedResizeMessage,
} from '../embed-protocol.js';
import {
  embedConfirmUrl,
  produceOwnershipSignatureReply,
} from '../verifier-embed.js';
import '../styles/verifier-confirm-modal.css';

/**
 * COB-263 — the verifier-served confirm embed, hosted in a user-app modal.
 *
 * Mirrors the KYC modal pattern (`kyc-modal.tsx`): a full-screen overlay
 * with a card that frames a third-party-origin `<iframe>`. Here the frame
 * is the verifier's `GET /embed/confirm/{id}`, and independence comes from
 * origin isolation — the contract is rendered by the verifier's own origin,
 * so a compromised user-app cannot forge benign terms (COB-245).
 *
 * The direction of trust inverts the sign-verifier-challenge popup: the
 * verifier embed is the CHILD and this host is the PARENT that signs the
 * ownership challenge with the in-context key-agent (no popup). All the
 * security-critical logic (origin-pin, refuse-foreign-vault, reconstruct-
 * the-canonical-message) lives in `verifier-embed.ts`; this component wires
 * the postMessage plumbing to it.
 *
 * The `confirmed` message is ADVISORY: it only closes the modal + triggers
 * a refetch. The deposit-address reveal stays gated server-side on the
 * verifier's ack-status (fail-closed), so a spoofed `confirmed` reveals
 * nothing.
 */

interface VerifierConfirmModalProps {
  vaultId: string;
  /** The verifier origin to frame + origin-pin, already resolved for the
   *  current network (see `verifierOriginForNetwork`). */
  verifierOrigin: string;
  /** User dismissed the modal (close button / overlay / Esc). */
  onClose: () => void;
  /** The embed reported an (advisory) confirm — the host should close and
   *  refetch `/vault/:id/details` to pick up the now-revealed address. */
  onConfirmed: () => void;
}

/** Iframe height before the embed reports its content height, and the
 *  clamp we apply to any reported height (defence against a hostile-but-
 *  same-origin resize spam). */
const DEFAULT_EMBED_HEIGHT = 420;
const MIN_EMBED_HEIGHT = 200;
const MAX_EMBED_HEIGHT = 2000;

export function VerifierConfirmModal({
  vaultId,
  verifierOrigin,
  onClose,
  onConfirmed,
}: VerifierConfirmModalProps) {
  const session = useSession();
  const [height, setHeight] = useState(DEFAULT_EMBED_HEIGHT);
  const [ready, setReady] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const src = embedConfirmUrl(verifierOrigin, vaultId);

  // Keep the message handler free of stale closures without re-subscribing
  // on every render: read the latest session + callbacks through refs.
  const sessionRef = useRef(session);
  sessionRef.current = session;
  const onConfirmedRef = useRef(onConfirmed);
  onConfirmedRef.current = onConfirmed;

  useEffect(() => {
    async function onMessage(ev: MessageEvent): Promise<void> {
      // Origin pin FIRST — a matching shape from the wrong origin (a
      // web3auth / hCaptcha / drive-by frame) is still hostile.
      if (ev.origin !== verifierOrigin) return;
      const data: unknown = ev.data;

      if (isEmbedReadyMessage(data)) {
        setReady(true);
        return;
      }

      if (isEmbedResizeMessage(data)) {
        setReady(true);
        setHeight(
          Math.min(MAX_EMBED_HEIGHT, Math.max(MIN_EMBED_HEIGHT, Math.ceil(data.height))),
        );
        return;
      }

      if (isEmbedRequestSignature(data)) {
        // Best-effort: heal a missing local derivation-index mapping the
        // same way sign-verifier-challenge does. Never blocks signing.
        let derivationIndexHint: number | undefined;
        const s = sessionRef.current;
        if (s.isAuthenticated) {
          try {
            const details = await api.getVaultDetails(vaultId);
            if (typeof details.derivation_index === 'number') {
              derivationIndexHint = details.derivation_index;
            }
          } catch {
            /* fall through — let the key-agent try its local mapping */
          }
        }
        const reply = await produceOwnershipSignatureReply({
          expectedVaultId: vaultId,
          requestedVaultId: data.vault_id,
          nonceHex: data.nonce_hex,
          agent: sessionRef.current.agent,
          derivationIndexHint,
        });
        iframeRef.current?.contentWindow?.postMessage(reply, verifierOrigin);
        return;
      }

      if (isEmbedConfirmedFor(data, vaultId)) {
        // Advisory only — the reveal stays server-gated. Close + refetch.
        onConfirmedRef.current();
        return;
      }

      if (isEmbedErrorMessage(data)) {
        // The embed renders its own error + a direct-verifier fallback
        // inside the frame; nothing for the host to do but leave it open.
        return;
      }
    }

    const handler = (ev: MessageEvent): void => {
      void onMessage(ev);
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [verifierOrigin, vaultId]);

  // Esc closes the modal.
  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      class="verifier-confirm-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="verifier-confirm-title"
      onClick={onClose}
    >
      <div class="verifier-confirm-card" onClick={(e) => e.stopPropagation()}>
        <div class="verifier-confirm-head">
          <h2 id="verifier-confirm-title">Confirm with the independent verifier</h2>
          <button
            type="button"
            class="verifier-confirm-close"
            aria-label="Close"
            onClick={onClose}
          >
            ×
          </button>
        </div>
        {!ready && (
          <p class="verifier-confirm-status" role="status">
            Connecting to the independent verifier…
          </p>
        )}
        <iframe
          ref={iframeRef}
          class="verifier-confirm-frame"
          src={src}
          title="Independent verifier — confirm your vault contract"
          style={{ height: `${height}px` }}
        />
        <p class="verifier-confirm-foot">
          Shown by the independent verifier at{' '}
          <span class="mono">{verifierOrigin.replace(/^https?:\/\//, '')}</span>.{' '}
          <a
            class="verifier-confirm-popout"
            href={`${verifierOrigin}/v/${encodeURIComponent(vaultId)}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open on the verifier ↗
          </a>
        </p>
      </div>
    </div>
  );
}
